import React, { useState, useEffect } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { uniq, compact, filter, some, uniqBy } from 'lodash-es';

import helpers from '../../utils/helpers';

const MultiEmailInput = ({ maxEmailsCount, disabled, onChange, onFocus, onBlur }) => {
  const [emails, setEmails] = useState([]);
  const [emailValue, setEmailValue] = useState('');

  const isLimitReached = emails.length >= maxEmailsCount;
  const isDuplicate = some(emails, ['value', emailValue]);
  const hasInvalid = some(emails, ['isValid', false]);
  const hasValidEmail = some(emails, ['isValid', true]);

  useEffect(() => {
    if (onChange) onChange(emails);
  }, [emails.length]);

  const handleChange = ({ target }) => {
    if (disabled) return;
    setEmailValue(target.value);
  };

  const handleBlur = () => {
    const emailsList = compact(emailValue.split(' '));
    const compEmailsLeg = emailsList.map((tabEmail) => ({ value: tabEmail }));
    const compEmails = [...emails, ...compEmailsLeg];
    const uniqEmailsList = uniqBy(compEmails, (e) => e.value);
    const nextEmails = uniqEmailsList.map(({ value }) => ({
      value,
      isValid: helpers.validateEmail(value),
    }));
    const sortedByValid = nextEmails.slice(0, maxEmailsCount);
    setEmails(sortedByValid);
    setEmailValue('');
    onBlur(hasValidEmail);
  };

  const handleKeyPress = (event) => {
    if (disabled) return;

    if (['Enter', ' ', ','].includes(event.key)) {
      if (!emailValue || isDuplicate) return;
      const emailsList = compact(emailValue.split(' '));
      const uniqEmailsList = filter(uniq(emailsList), (email) => !some(emails, ['value', email]));
      const nextEmails = uniqEmailsList.slice(0, maxEmailsCount - emails.length).map((value) => ({
        value,
        isValid: helpers.validateEmail(value),
      }));

      setEmails((prevState) => [...prevState, ...nextEmails]);
      setEmailValue('');
    }
  };

  const handleRemoveTag = (tag) => {
    if (disabled) return;
    setEmails((prevState) => prevState.filter(({ value }) => value !== tag));
  };

  const placeholderText = () => {
    if (!emails.length) return `Enter up to ${maxEmailsCount} email addresses`;
    if (maxEmailsCount > emails.length) return `Add up to ${maxEmailsCount - emails.length} more…`;
    return '';
  };

  return (
    <div className="multi-emails-container">
      <div className={cx('multi-emails', { disabled })}>
        <div className="multi-emails-tags">
          {emails.map(({ value, isValid }, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div
              key={`${value}-${i}`}
              className={cx('multi-emails-tag', { 'multi-emails-tag-invalid': !isValid })}
            >
              <span className="multi-emails-tag-name text-truncate">{value}</span>
              <span className="hicon-close-2" onClick={() => handleRemoveTag(value)} />
            </div>
          ))}
        </div>

        {!isLimitReached && (
          <div className="multi-emails-input">
            <input
              type="text"
              value={emailValue}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              placeholder={placeholderText()}
              onFocus={onFocus}
              onBlur={handleBlur}
            />
          </div>
        )}
      </div>
      {isDuplicate && <p className="multi-emails-error">You&apos;ve already typed this email</p>}
      {hasInvalid && (
        <p className="multi-emails-error">One or more of the email addresses is invalid</p>
      )}
    </div>
  );
};

MultiEmailInput.propTypes = {
  disabled: T.bool,
  maxEmailsCount: T.number.isRequired,
  onChange: T.func,
  onFocus: T.func,
  onBlur: T.func,
};

export default MultiEmailInput;
